import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Download,
  UserCheck,
  UserX,
  Heart,
  Target,
  FileSpreadsheet,
  Shield,
  Activity,
  BarChart3,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Customer360Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/customer-360`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load Customer360 dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/customer-360/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') return value;
    return value?.toLocaleString() || '0';
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="customer-360-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="customer-360-no-data">
        <Users className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonCustomer360 agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'} data-testid="goto-agents-btn">Go to Agents</Button>
      </div>
    );
  }

  const overview = data?.customerOverview || {};
  const rfmSegments = data?.rfmSegmentation || [];
  const churnRisk = data?.churnRisk || {};
  const segmentMetrics = data?.segmentMetrics || {};
  const retention = data?.retention || {};
  const dataQuality = data?.dataQuality || {};
  const hcvLeaders = data?.hcvLeaders || [];
  const additionalNotes = data?.additionalNotes || [];
  const deltaAnalysis = data?.deltaAnalysis || {};

  const COLORS = ['#8b5cf6', '#3b82f6', '#22c55e', '#f97316', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#f43f5e', '#a855f7'];

  return (
    <div className="space-y-6" data-testid="customer-360-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="h-8 w-8 text-purple-600" />
            <TranslatedText text="Customer360 Dashboard" cacheKey="customer360:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Deterministic Customer Intelligence & Retention Analysis" cacheKey="customer360:subtitle" />
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('all_customers', 'All Customers')} data-testid="export-all-btn">
            <Download className="h-4 w-4 mr-2" />Export All
          </Button>
        </div>
      </div>

      {/* Data Quality Banner */}
      {dataQuality.shortHistoryFlag && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="short-history-warning">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Short history detected ({dataQuality.observationWindowDays} days). Some metrics may have limited reliability.</span>
        </div>
      )}

      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 border-purple-200" data-testid="exec-summary-card">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="customer360:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          {data?.summary ? (
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
              <TranslatedText text={data.summary} cacheKey="customer360:summary" />
            </p>
          ) : (
            <p className="text-slate-400 italic">No executive summary available for this run.</p>
          )}
        </CardContent>
      </Card>

      {/* Key Metrics — P2 Deterministic */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="key-metrics-grid">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-700"><TranslatedText text="Total Customers" cacheKey="customer360:total_customers" /></p>
            <p className="text-xl font-bold text-purple-900" data-testid="metric-total-customers">{formatNumber(overview.totalCustomers)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700"><TranslatedText text="Repeat Rate" cacheKey="customer360:repeat_rate" /></p>
            <p className="text-xl font-bold text-green-900" data-testid="metric-repeat-rate">{overview.repeatRate || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700"><TranslatedText text="Avg HCV" cacheKey="customer360:avg_hcv" /></p>
            <p className="text-xl font-bold text-blue-900" data-testid="metric-avg-hcv">{formatCurrency(overview.avgHCV)}</p>
            <p className="text-[10px] text-blue-500 mt-0.5">Historical Customer Value</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <CardContent className="pt-4">
            <p className="text-xs text-cyan-700"><TranslatedText text="Avg NCV" cacheKey="customer360:avg_ncv" /></p>
            <p className="text-xl font-bold text-cyan-900" data-testid="metric-avg-ncv">{formatCurrency(overview.avgNCV)}</p>
            <p className="text-[10px] text-cyan-500 mt-0.5">Net Customer Value</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700"><TranslatedText text="Avg Basket Size" cacheKey="customer360:avg_basket" /></p>
            <p className="text-xl font-bold text-orange-900" data-testid="metric-avg-basket">{formatCurrency(overview.avgBasketSize)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700"><TranslatedText text="Churn Risk" cacheKey="customer360:churn_risk" /></p>
            <p className="text-xl font-bold text-red-900" data-testid="metric-churn-risk">{formatNumber(churnRisk.highRiskCustomers)}</p>
            <p className="text-[10px] text-red-500 mt-0.5">Rule-Based Score</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="pt-4">
            <p className="text-xs text-indigo-700"><TranslatedText text="Retention Rate" cacheKey="customer360:retention_rate" /></p>
            <p className="text-xl font-bold text-indigo-900" data-testid="metric-retention-rate">
              {retention.status === 'COMPUTED' && retention.averageRetentionRate != null
                ? `${retention.averageRetentionRate}%`
                : 'N/A'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Segment Distribution Bar */}
      {(segmentMetrics.vip > 0 || segmentMetrics.loyal > 0 || segmentMetrics.active > 0 || segmentMetrics.new > 0 || segmentMetrics.atRisk > 0 || segmentMetrics.lost > 0) && (
        <Card data-testid="segment-distribution-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Customer Segment Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'VIP', count: segmentMetrics.vip, color: 'bg-purple-100 text-purple-800 border-purple-200' },
                { label: 'Loyal', count: segmentMetrics.loyal, color: 'bg-blue-100 text-blue-800 border-blue-200' },
                { label: 'Active', count: segmentMetrics.active, color: 'bg-green-100 text-green-800 border-green-200' },
                { label: 'New', count: segmentMetrics.new, color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
                { label: 'At Risk', count: segmentMetrics.atRisk, color: 'bg-orange-100 text-orange-800 border-orange-200' },
                { label: 'Lost', count: segmentMetrics.lost, color: 'bg-red-100 text-red-800 border-red-200' },
              ].map((seg) => (
                <div key={seg.label} className={`p-3 rounded-lg border ${seg.color} text-center`}>
                  <p className="text-xs font-medium">{seg.label}</p>
                  <p className="text-2xl font-bold">{formatNumber(seg.count)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RFM Segmentation + Churn Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RFM Segmentation */}
        <Card data-testid="rfm-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>RFM Segmentation</CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('rfm_segments', 'RFM Segments')} disabled={exporting === 'rfm_segments'} data-testid="export-rfm-btn">
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {rfmSegments.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={rfmSegments}
                      dataKey="customerCount"
                      nameKey="segment"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ segment, pctOfTotal }) => `${segment}: ${pctOfTotal}`}
                    >
                      {rfmSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {rfmSegments.slice(0, 6).map((seg, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                        <span className="font-medium text-sm">{seg.segment}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm">{formatNumber(seg.customerCount)}</span>
                        <span className="text-xs text-slate-500 ml-2">({seg.pctOfTotal})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400" data-testid="rfm-no-data">
                <BarChart3 className="h-10 w-10 mb-2" />
                <p>No RFM segmentation data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Churn Risk Analysis — Rule-Based */}
        <Card className="border-red-200" data-testid="churn-risk-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Churn Risk Analysis
                </CardTitle>
                <CardDescription className="flex items-center gap-1 mt-1">
                  <Shield className="h-3 w-3" />
                  Rule-based deterministic scoring (not predictive)
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleExport('churn_risk', 'Churn Risk')} disabled={exporting === 'churn_risk'} data-testid="export-churn-btn">
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-700">High Risk Customers</p>
                <p className="text-2xl font-bold text-red-900" data-testid="churn-high-risk-count">{formatNumber(churnRisk.highRiskCustomers)}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-700">Revenue at Risk</p>
                <p className="text-2xl font-bold text-orange-900" data-testid="churn-revenue-at-risk">{formatCurrency(churnRisk.churnRiskValue)}</p>
              </div>
            </div>
            {churnRisk.topChurnReasons && churnRisk.topChurnReasons.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Top Risk Factors (Deterministic):</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  {churnRisk.topChurnReasons.map((reason, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(churnRisk.atRiskSegment || []).length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {churnRisk.atRiskSegment.slice(0, 5).map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                    <span className="font-mono text-xs">{customer.customerId}</span>
                    <div className="text-right flex items-center gap-3">
                      <span className="text-red-600 font-medium">{customer.riskScore}</span>
                      <span className="text-slate-500 text-xs">{customer.lastPurchase}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm text-center py-4" data-testid="churn-no-data">No high-risk customers identified</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* HCV Leaders */}
      {hcvLeaders.length > 0 && (
        <Card data-testid="hcv-leaders-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Top Customers by Historical Value (HCV)
                </CardTitle>
                <CardDescription>Observed total net revenue — deterministic, not predicted</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">#</th>
                    <th className="pb-2 pr-4">Customer</th>
                    <th className="pb-2 pr-4">Segment</th>
                    <th className="pb-2 pr-4 text-right">HCV</th>
                    <th className="pb-2 pr-4 text-right">NCV</th>
                    <th className="pb-2 text-right">Frequency</th>
                  </tr>
                </thead>
                <tbody>
                  {hcvLeaders.map((leader, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 pr-4 text-slate-400">{idx + 1}</td>
                      <td className="py-2 pr-4 font-medium">{leader.name || leader.customerId}</td>
                      <td className="py-2 pr-4">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">{leader.segment}</span>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">{formatCurrency(leader.hcv)}</td>
                      <td className="py-2 pr-4 text-right font-mono">{formatCurrency(leader.ncv)}</td>
                      <td className="py-2 text-right">{leader.frequency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Retention Metrics */}
      <Card data-testid="retention-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-600" />
            Period Retention
          </CardTitle>
          <CardDescription>
            Of customers active in period P(n-1), what % were also active in P(n)?
          </CardDescription>
        </CardHeader>
        <CardContent>
          {retention.status === 'COMPUTED' ? (
            <>
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <p className="text-xs text-indigo-600">Average Retention</p>
                  <p className="text-2xl font-bold text-indigo-900" data-testid="retention-avg-rate">{retention.averageRetentionRate}%</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600">Periods Analyzed</p>
                  <p className="text-2xl font-bold text-slate-900">{retention.periodsAnalyzed}</p>
                </div>
              </div>
              {retention.monthlyRetention && retention.monthlyRetention.length > 0 && (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={retention.monthlyRetention}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period_to" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="retention_rate" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-slate-400" data-testid="retention-not-computable">
              <Activity className="h-8 w-8 mb-2" />
              <p className="text-sm">Retention data not computable</p>
              <p className="text-xs mt-1">Requires at least 2 months of transaction history</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Quality & Analysis Context */}
      <Card className="bg-slate-50/50" data-testid="data-quality-card">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('dataQuality')}>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-slate-500" />
              Data Quality & Analysis Context
            </CardTitle>
            {expandedSections.dataQuality ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {expandedSections.dataQuality && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">Observation Window</p>
                <p className="font-medium">{dataQuality.observationWindowDays || 0} days</p>
              </div>
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">As-Of Date</p>
                <p className="font-medium">{dataQuality.asOfDate ? new Date(dataQuality.asOfDate).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">Short History</p>
                <p className={`font-medium ${dataQuality.shortHistoryFlag ? 'text-amber-600' : 'text-green-600'}`}>
                  {dataQuality.shortHistoryFlag ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="p-2 bg-white rounded border">
                <p className="text-xs text-slate-500">Future-Dated Excluded</p>
                <p className="font-medium">{dataQuality.futureDatedRecordsExcluded || 0}</p>
              </div>
            </div>
            {additionalNotes.length > 0 && (
              <div className="mt-4 space-y-1">
                <p className="text-xs font-medium text-slate-500">Methodology Notes:</p>
                {additionalNotes.map((note, idx) => (
                  <p key={idx} className="text-xs text-slate-500">• {note}</p>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Export Full Customer Data */}
      <Card className="bg-slate-50" data-testid="export-section">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Export Full Customer Data</h3>
              <p className="text-sm text-slate-500">Download complete customer database for analysis</p>
            </div>
            <Button onClick={() => handleExport('all_customers', 'All Customers')} disabled={exporting === 'all_customers'} data-testid="export-customers-btn">
              <Download className="h-4 w-4 mr-2" />
              {exporting === 'all_customers' ? 'Exporting...' : 'Export All Customers'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Customer360Dashboard;
